import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ElementCard } from '@/components/content/ElementCard';

/**
 * The two-doors block on the home page.
 * PRD-07 §7.4.3 — side-by-side cards, equal weight, identical structure.
 * Mobile: developer card stacks first (bigger existing audience).
 */

const PLATFORM_GITHUB_URL = 'https://github.com/Unicorn/Radium';

interface DoorCardProps {
  side: 'platform' | 'personalization';
  symbol: string;
  number: string;
  label: string;
  headline: string;
  tagline: string;
  body: string;
  bullets: string[];
  primary: { to: string; label: string; external?: boolean };
  secondary: { to: string; label: string; external?: boolean };
}

function DoorCard({
  side,
  symbol,
  number,
  label,
  headline,
  tagline,
  body,
  bullets,
  primary,
  secondary,
}: DoorCardProps) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        padding: '40px 36px',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        background: 'var(--bg-page)',
        transition: 'background 240ms var(--ease-out)',
        overflow: 'hidden',
        minHeight: 560,
      }}
    >
      {/* Side-specific glow that warms only on hover */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at ${side === 'platform' ? '20% 25%' : '80% 25%'}, var(--radium-glow) 0%, transparent 55%)`,
          opacity: hover ? 1 : 0,
          transition: 'opacity 320ms var(--ease-out)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <ElementCard symbol={symbol} number={number} label={label} size="md" />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2
          className="text-h3"
          style={{ marginBottom: 8, color: 'var(--ink-primary)' }}
        >
          {headline}
        </h2>
        <div
          style={{
            color: 'var(--accent)',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            letterSpacing: '0.04em',
            marginBottom: 20,
          }}
        >
          {tagline}
        </div>
        <p
          style={{
            color: 'var(--ink-secondary)',
            fontSize: 15.5,
            lineHeight: 1.7,
          }}
        >
          {body}
        </p>
      </div>

      <ul
        style={{
          position: 'relative',
          zIndex: 1,
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {bullets.map((b) => (
          <li
            key={b}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 12,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: 'var(--ink-secondary)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: 'var(--accent)',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              ◉
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          marginTop: 'auto',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {primary.external ? (
          <a
            href={primary.to}
            target="_blank"
            rel="noopener noreferrer"
            style={primaryCTAStyle}
          >
            {primary.label}{' '}
            <span aria-hidden="true" style={{ marginLeft: 4 }}>
              →
            </span>
          </a>
        ) : (
          <Link to={primary.to} style={primaryCTAStyle}>
            {primary.label}{' '}
            <span aria-hidden="true" style={{ marginLeft: 4 }}>
              →
            </span>
          </Link>
        )}
        {secondary.external ? (
          <a
            href={secondary.to}
            target="_blank"
            rel="noopener noreferrer"
            style={secondaryCTAStyle}
          >
            {secondary.label}{' '}
            <span aria-hidden="true" style={{ marginLeft: 4 }}>
              →
            </span>
          </a>
        ) : (
          <Link to={secondary.to} style={secondaryCTAStyle}>
            {secondary.label}{' '}
            <span aria-hidden="true" style={{ marginLeft: 4 }}>
              →
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}

const primaryCTAStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 20px',
  borderRadius: 6,
  background: 'var(--accent)',
  color: 'var(--ink-on-accent)',
  fontWeight: 500,
  fontSize: 14.5,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const secondaryCTAStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 500,
  fontSize: 14.5,
  color: 'var(--accent)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

export function TwoDoorsBlock() {
  return (
    <section
      id="two-doors"
      style={{ position: 'relative', padding: '64px 0 96px' }}
    >
      <div className="container container-wide">
        <div
          className="two-doors-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            overflow: 'hidden',
            // Vertical hairline between the cards via a center column gap on desktop
            gap: '0',
            position: 'relative',
          }}
        >
          {/* Orchestration / OSS card */}
          <DoorCard
            side="platform"
            symbol="Or"
            number="08"
            label="Orchestration"
            headline="Radium Orchestration"
            tagline="Open-source. Multi-agent. Production-ready."
            body="A Rust runtime for multi-agent DAG workflows, a policy engine for governed execution, and metacognitive oversight that prevents reasoning lock-in. Provider-agnostic. Self-hostable. MIT-licensed."
            bullets={[
              'Multi-agent DAG workflows in TOML',
              'Policy engine with approval modes',
              'Vibe Check oversight: +27% task success, −41% harmful actions',
            ]}
            primary={{ to: '/docs/introduction', label: 'Read the docs' }}
            secondary={{
              to: PLATFORM_GITHUB_URL,
              label: 'Star on GitHub',
              external: true,
            }}
          />

          {/* Vertical hairline (desktop only) */}
          <div
            aria-hidden="true"
            className="two-doors-hairline"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 1,
              background: 'var(--border-default)',
              pointerEvents: 'none',
            }}
          />

          {/* Personalization / Enterprise card */}
          <DoorCard
            side="personalization"
            symbol="Pe"
            number="14"
            label="Personalization"
            headline="Radium Personalization"
            tagline="Enterprise content decisioning. On the surfaces you own."
            body="A productized application of the Radium platform. Content intelligence, generative recommendations, sub-second in-session ranking, and a human-in-the-loop admin — for teams who want personalization to feel earned."
            bullets={[
              'Aligned to Jobs-To-Be-Done, not just demographics',
              'Generative copy and module variants in-session',
              'Built on the open-source Radium runtime',
            ]}
            primary={{ to: '/personalization', label: 'See the product' }}
            secondary={{
              to: '/contact',
              label: 'Contact us',
            }}
          />
        </div>
      </div>
      <style>{`
        @media (max-width: 720px) {
          .two-doors-grid {
            grid-template-columns: 1fr !important;
          }
          .two-doors-hairline {
            top: 50% !important;
            bottom: auto !important;
            left: 0 !important;
            right: 0 !important;
            width: auto !important;
            height: 1px !important;
          }
        }
      `}</style>
    </section>
  );
}
